import { useState, type Dispatch, type SetStateAction } from "react";
import type { LessonAiDraft, LessonWorkspaceMode } from "../../auth/api-client";
import { AutoResizeTextarea } from "../../components/AutoResizeTextarea";
import { LessonIcon } from "../LessonIcon";
import { LessonIconPickerModal } from "../LessonIconPickerModal";
import { formatLessonIconLabel } from "../lessonIcons";
import { syncMainPyInStarterFiles } from "../course-template-helpers";

export interface LessonDetailsSectionProps {
  readonly draft: LessonAiDraft;
  readonly setDraft: Dispatch<SetStateAction<LessonAiDraft>>;
  readonly savedLessonId: string | null;
  readonly creationPrompt: string | null;
}

export function LessonDetailsSection({
  draft,
  setDraft,
  savedLessonId,
  creationPrompt,
}: LessonDetailsSectionProps) {
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const iconLabel = draft.iconName ? formatLessonIconLabel(draft.iconName) : "Gradient tile";

  return (
    <div className="lesson-ws-section">
      <p className="muted small lesson-ws-section-lede">
        How this lesson looks in your library and which workspace students get. Title is edited in the
        header above.
      </p>

      <label className="field">
        <span className="field-label">Description</span>
        <AutoResizeTextarea
          className="input"
          minRows={2}
          value={draft.description ?? ""}
          onChange={(ev) => setDraft((d) => ({ ...d, description: ev.target.value }))}
          placeholder="One or two sentences about what students build and practice."
        />
      </label>

      <label className="field">
        <span className="field-label">Slug (optional)</span>
        <input
          className="input"
          value={draft.slug ?? ""}
          onChange={(ev) => setDraft((d) => ({ ...d, slug: ev.target.value }))}
          placeholder="auto from title if left blank"
        />
      </label>

      <div className="teacher-lesson-appearance">
        <div className="field teacher-lesson-appearance-icon">
          <span className="field-label" id="lesson-icon-picker-label">
            Icon
          </span>
          <button
            type="button"
            className="input lesson-icon-picker-trigger"
            aria-labelledby="lesson-icon-picker-label"
            aria-haspopup="dialog"
            onClick={() => setIconPickerOpen(true)}
          >
            <LessonIcon
              lessonId={savedLessonId ?? "draft"}
              title={draft.title}
              iconName={draft.iconName}
              bgColor={draft.iconBgColor}
              fgColor={draft.iconFgColor}
              workspaceMode={draft.workspaceMode}
              size={1.75}
            />
            <span className="lesson-icon-picker-trigger-label">{iconLabel}</span>
          </button>
          <LessonIconPickerModal
            open={iconPickerOpen}
            selectedIconName={draft.iconName}
            bgColor={draft.iconBgColor}
            fgColor={draft.iconFgColor}
            lessonId={savedLessonId ?? "draft"}
            title={draft.title}
            workspaceMode={draft.workspaceMode}
            onClose={() => setIconPickerOpen(false)}
            onSelect={(iconName) => setDraft((d) => ({ ...d, iconName }))}
          />
        </div>
        <label className="field teacher-lesson-appearance-color">
          <span className="field-label">Background</span>
          <input
            className="input teacher-color-input"
            type="color"
            value={draft.iconBgColor ?? "#1b4d2e"}
            onChange={(ev) => setDraft((d) => ({ ...d, iconBgColor: ev.target.value }))}
          />
        </label>
        <label className="field teacher-lesson-appearance-color">
          <span className="field-label">Icon color</span>
          <input
            className="input teacher-color-input"
            type="color"
            value={draft.iconFgColor ?? "#fdf9f3"}
            onChange={(ev) => setDraft((d) => ({ ...d, iconFgColor: ev.target.value }))}
          />
        </label>
        <LessonIcon
          lessonId={savedLessonId ?? "draft"}
          title={draft.title}
          iconName={draft.iconName}
          bgColor={draft.iconBgColor}
          fgColor={draft.iconFgColor}
          workspaceMode={draft.workspaceMode}
          size={3.25}
          className="teacher-lesson-appearance-preview"
        />
      </div>

      <label className="field">
        <span className="field-label">Student experience</span>
        <select
          className="input"
          value={draft.workspaceMode ?? "simple"}
          onChange={(ev) => {
            const mode = ev.target.value as LessonWorkspaceMode;
            setDraft((d) => {
              if (mode === "advanced") {
                return {
                  ...d,
                  workspaceMode: "advanced",
                  starterFiles: syncMainPyInStarterFiles(d.starterFiles ?? [], d.mainPy),
                };
              }
              return { ...d, workspaceMode: "simple", starterFiles: undefined };
            });
          }}
        >
          <option value="simple">Simple: editor, output, and instructions side by side</option>
          <option value="advanced">Advanced: full-page multi-file workspace with file browser</option>
        </select>
        <p className="muted small" style={{ marginTop: "0.35rem", marginBottom: 0 }}>
          Most lessons stay in simple mode. Use advanced only when the activity needs helper modules or
          multiple files.
        </p>
      </label>

      {creationPrompt ? (
        <p className="muted small lesson-ws-creation-prompt">
          <strong>Created from prompt:</strong> {creationPrompt}
        </p>
      ) : null}
    </div>
  );
}
